import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchFile } from "../../Services/api";
import instance from "../../redux/api";
import { downloadFileModdules } from "./constants";
import { useFileUpload } from "./hooks";
import { commonToast } from "../common";

const WrideDetails = {
  height: 50,
  width: "calc(100% - 35px)",
  padding: 15,
};


const History = ({ history }) => {
  const [downloadingFiles, setDownloadingFiles] = useState({});

  const handleDownloadFile = async (bucketKey, documentName) => {
    try {
      setDownloadingFiles((prev) => ({ ...prev, [bucketKey]: true }));
      const module = downloadFileModdules.cashAllocation;
      const url = `${fetchFile}?key=${bucketKey}&module=${module}`;
  
      const response = await instance.get(url, { responseType: "blob" });
      const contentType = response.headers["content-type"];
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);
  
      if (contentType.includes("pdf")) {        
        window.open(fileURL, "_blank");
      } else {
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = documentName || bucketKey.split('/').pop() || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      showToast("success", "File downloaded successfully.");
    } catch (error) {
      showToast("error", "Failed to open the file.");
    } finally {
      setDownloadingFiles((prev) => {
        const updated = { ...prev };
        delete updated[bucketKey];
        return updated;
      });
    }
  }

  const showToast = (type, message) => {
    toast[type](message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 5000,
    });
  };

  return (
    <div className="cash-allocation-dialogue-container">
      <TableContainer sx={{ height: 400, overflow: "auto" }}>
        <Table>
          <TableHead className="table-head">
            <TableRow>
              <TableCell className="table-cell">File Name</TableCell>
              <TableCell className="table-cell p-0">File Details</TableCell>
              <TableCell className="table-cell">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="table-body">
            {history?.map((row, index) => (
              <TableRow key={index} className="table-row" sx={{ height: "50px" }}>
                <TableCell className="table-cell">
                  {row.document_name}
                </TableCell>
                <TableCell className="table-cell">
                  {row.document_details}
                </TableCell>
                <TableCell className="table-cell">
                {downloadingFiles[row.document_file] ? (
                  <CircularProgress
                    style={{ color: "rgb(255, 90, 1)" }}
                    size={30} // Adjust the size as needed
                  />
                ) : (
                  <DownloadIcon
                    style={{ marginRight: 8, cursor: "pointer", color: "#FF5A01" }}
                    titleAccess="Download File"
                    color="green"
                    onClick={() => handleDownloadFile(row.document_file, row.document_name)}
                  />
                )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

const FileUpload = ({ transaction }) => {
  const { history, formData, setFormData, submit } = useFileUpload(transaction);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setShowHistory(!!history.length);
  }, [history?.length]);

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, [setIsDialogOpen]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setFormData({});
    setShowHistory(true);
  }, [setIsDialogOpen]);

  const handleSubmitUpload = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      await submit();
      setLoading(false);
      setFormData({});
      setShowHistory(true);
    },
    [setIsDialogOpen, submit]
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name, value, files } = e.target;
      const allowedFileTypes = [
        "text/csv",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/jpg"
      ];

      if (name === "file" && files.length > 0) {
        const fileArray = Array.from(files);

        const validFiles = fileArray.filter((currentFile) =>
          allowedFileTypes.includes(currentFile.type)
        );

        if (validFiles.length === 0) {
          commonToast("error", "Unsupported file format.");
          setFormData({});
          return;
        };

        if (validFiles.length > 5) {
          commonToast("error", "Maximum of 5 files only can be uploaded at a time");
          setFormData({});
          return;
        }

        fileArray.forEach((currentFile) => {
          if (currentFile?.size > 15 * 1024 * 1024) {
            commonToast("error", "Maximum allowed file size is 15MB");
            setFormData({});
            return;
          }
        });

        setFormData((data) => ({
          ...data,
          [name]: validFiles,
        }));
      }
    },
    [setFormData]
  );

  const handleTextInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((data) => ({
      ...data,
      [name]: value,
    }));
  };

  return (
    <>
      <span className="file-download-icon" onClick={handleOpenDialog}>
        Files: <AttachFileIcon />
      </span>
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth>
        <form onSubmit={handleSubmitUpload}>
          <DialogTitle style={{ background: "#FCFCFC" }}>Files</DialogTitle>
          <DialogContent>
            {showHistory ? (
              <History setShowHistory={setShowHistory} history={history} />
            ) : (
              <>
                <Grid container>
                  <Grid item xs={12}>
                    <span style={{ fontSize: 13, color: "#17191B" }}>
                      File Details
                    </span>

                    <TextareaAutosize
                      style={WrideDetails}
                      name="document_details"
                      value={formData.document_details}
                      onChange={handleTextInputChange}
                      placeholder="Write details here"
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12} marginTop={2}>
                    <Button
                      color="inherit"
                      variant="outlined"
                      component="label"
                    >
                      Select New File to Upload
                      <input
                        type="file"
                        hidden
                        name="file"
                        accept=".doc,.docx,.xls,.xlsx,.csv,.pdf,.jpg,.jpeg"
                        onChange={handleInputChange}
                        multiple
                        required
                        disabled={loading}
                      />
                    </Button>
                    {formData?.file?.length > 0 && (
                      <>
                        <span style={{ fontSize: 13, marginTop: "15px" }}>
                          Selected Files
                        </span>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            overflowY: "auto",
                            maxHeight: 300,
                          }}
                        >
                          {formData.file.map((item) => {
                            return (
                              <span className="file-name">{item?.name}</span>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          <DialogActions
            style={{ justifyContent: "flex-end", padding: "15px 25px" }}
          >
            <Button
              onClick={() => setShowHistory(!showHistory)}
              color={showHistory ? "warning" : "inherit"}
              variant={showHistory ? "contained" : "outlined"}
              sx={{ borderRadius: 100 }}
              disabled={loading}
            >
              {showHistory ? "Add" : "History"}
            </Button>
            <Button
              onClick={handleCloseDialog}
              color="inherit"
              variant="outlined"
              sx={{ borderRadius: 100, border: "1px solid #BCBDBF" }}
              disabled={loading}
            >
              Close
            </Button>
            {!showHistory &&
              (loading ? (
                <LoadingButton loading>Uploading...</LoadingButton>
              ) : (
                <Button
                  variant="contained"
                  color="warning"
                  sx={{ borderRadius: 100 }}
                  type="submit"
                >
                  Upload
                </Button>
              ))}
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default FileUpload;
